import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Trash2, Plus, Save, Loader } from 'lucide-react';
import { HERO_SLIDES } from '../../../constants';
import ImageUploader from '../common/ImageUploader';

import { HeroSlide } from '../../../types';

const HeroManager: React.FC = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const docRef = doc(db, 'content', 'home');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().heroSlides) {
        setSlides(docSnap.data().heroSlides);
      } else {
        setSlides(HERO_SLIDES);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await setDoc(doc(db, 'content', 'home'), {
        heroSlides: slides,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      setMessage({ type: 'success', text: 'Hero content saved successfully' });
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage({ type: 'error', text: 'Failed to save content' });
    } finally {
      setSaving(false);
    }
  };

  const updateSlide = (index: number, field: keyof HeroSlide, value: string) => {
    const newSlides = [...slides];
    // @ts-ignore
    newSlides[index] = { ...newSlides[index], [field]: value };
    setSlides(newSlides);
  };

  const addSlide = () => {
    const newSlide: HeroSlide = {
      id: Date.now(),
      image: '/assets/placeholder.svg',
      title: '',
      subtitle: '',
      cta: '',
      link: '',
      signOff: ''
    };
    setSlides([...slides, newSlide]);
  };

  const removeSlide = (index: number) => {
    const newSlides = slides.filter((_, i) => i !== index);
    setSlides(newSlides);
  };

  if (loading) return <div className="py-8"><Loader className="animate-spin" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-xl">Hero Configuration</h2>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm"
        >
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </button>
      </div>

      {message && (
        <div className={`p-3 mb-6 text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex justify-between items-center bg-gray-50 p-3 border border-gray-200">
           <span className="font-mono text-xs text-gray-500 uppercase">Slides: {slides.length}</span>
           <button onClick={addSlide} className="flex items-center gap-2 text-xs font-bold text-brand-gold hover:text-black transition-colors uppercase tracking-wider">
            <Plus size={14} /> Add Slide
          </button>
        </div>

        {slides.map((slide, index) => (
          <div key={slide.id} className="bg-white border border-gray-200 p-6 shadow-sm">
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-bold text-gray-400 text-xs tracking-widest">SLIDE {index + 1}</h3>
              <button onClick={() => removeSlide(index)} className="text-gray-400 hover:text-red-500">
                <Trash2 size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <ImageUploader
                  value={slide.image === '/assets/placeholder.svg' ? '' : slide.image}
                  onChange={(url) => updateSlide(index, 'image', url || '/assets/placeholder.svg')}
                  pathPrefix="content/hero"
                  aspectClass="aspect-video"
                  onError={(text) => setMessage({ type: 'error', text })}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Title</label>
                  <input 
                    type="text" 
                    value={slide.title}
                    onChange={(e) => updateSlide(index, 'title', e.target.value)}
                    className="w-full p-2 border border-gray-300 font-heading font-bold" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={slide.subtitle}
                    onChange={(e) => updateSlide(index, 'subtitle', e.target.value)}
                    className="w-full p-2 border border-gray-300 font-body"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Sign-off</label>
                  <input
                    type="text"
                    value={slide.signOff || ''}
                    onChange={(e) => updateSlide(index, 'signOff', e.target.value)}
                    className="w-full p-2 border border-gray-300 font-mono text-xs"
                    placeholder="4HVNSK8 — Hastings."
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Quiet line under the subtitle. Optional.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                   <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">CTA Text</label>
                    <input
                      type="text"
                      value={slide.cta || ''}
                      onChange={(e) => updateSlide(index, 'cta', e.target.value)}
                      className="w-full p-2 border border-gray-300 font-bold text-xs"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Leave empty for no button (approved hero has none).</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Link URL</label>
                    <input
                      type="text"
                      value={slide.link || ''}
                      onChange={(e) => updateSlide(index, 'link', e.target.value)}
                      className="w-full p-2 border border-gray-300 font-mono text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HeroManager;
